import { Loader2, Users, Clock, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { SupportRequest } from '@/types/support';

function formatWait(sec?: number) {
  if (!sec || sec <= 0) return '~2 min';
  if (sec < 60) return `~${sec}s`;
  return `~${Math.ceil(sec / 60)} min`;
}

export function WaitingScreen({
  request,
  onlineAgents,
  onCancel,
  cancelling,
}: {
  request: SupportRequest;
  onlineAgents: number;
  onCancel: () => void;
  cancelling?: boolean;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 px-4 py-8 text-center h-full">
      <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
        <Loader2 className="h-6 w-6 text-primary animate-spin" />
      </div>
      <div>
        <h3 className="text-sm font-semibold text-foreground">Waiting for support…</h3>
        <p className="mt-1 text-xs text-muted-foreground max-w-[260px]">
          We notified our team. Stay on this page - an agent will join shortly.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3 w-full max-w-[280px] text-left">
        <div className="rounded-lg border border-border p-3">
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <Clock className="h-3.5 w-3.5" /> Est. wait
          </div>
          <div className="mt-1 text-sm font-medium">{formatWait(request.estimatedWaitSec)}</div>
        </div>
        <div className="rounded-lg border border-border p-3">
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            Queue position
          </div>
          <div className="mt-1 text-sm font-medium">#{request.queuePosition ?? 1}</div>
        </div>
        <div className="rounded-lg border border-border p-3 col-span-2">
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <Users className="h-3.5 w-3.5" /> Support online
          </div>
          <div className="mt-1 text-sm font-medium">
            {onlineAgents} agent{onlineAgents === 1 ? '' : 's'} available
          </div>
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground line-clamp-2 max-w-[280px]">
        Issue: {request.issueSummary}
      </p>
      <Button variant="outline" size="sm" onClick={onCancel} disabled={cancelling} className="gap-1.5">
        <X className="h-3.5 w-3.5" />
        Cancel request
      </Button>
    </div>
  );
}
